import { actionChannel, call, delay, put, select, take, takeEvery, takeLeading } from 'redux-saga/effects'
import { get } from 'lodash'
import { toast } from 'react-toastify'

import Stadium from '@ordernext/networking-stadium/stadium'
import Remote from '../../remote'

import {
  createOfflineOrderSucceeded,
  createOfflineOrderFailed,
  PRE_ORDERS_REQUESTED,
  loadPreOrdersSucceeded,
  loadPreOrdersFailed,
  REPLAY_AUTH_REQUESTED,
  replayAuthRequestSucceeded,
  replayAuthRequestFailed,
  saveOrder,
  SYNC_ORDER_REQUESTED,
  SEND_ORDER_EMAIL,
  syncOrderInitiated,
  syncOrderRequested,
  syncOrderSucceeded,
  syncOrderFailed,
  SYNC_OFFLINE_ORDER_REQUESTED,
  syncOfflineOrderRequested,
  updateOrderSucceeded,
  updateOrderFailed,
  ORDER_DETAILS_REQUESTED,
  loadOrderDetailsSucceeded,
  loadOrderDetailsFailed,
  loadOrdersDeltasSucceeded,
  loadOrdersDeltasFailed,
  ORDERS_DELTAS_REQUESTED,
} from '../../actions/order'

import { makeGetAttendantStandUuids } from '../../selectors/attendant'
import { makeGetOrder, getCurrentOrderId, getUnsyncedLocalIds } from '../../selectors/order'

import DataMapper from '../../utils/dataMapper'
import PeripheralBridge from '../../utils/peripheralBridge'
import {
  approvedOfflineThenDeclined,
  responseStatusString,
  statusCheckComplete
} from '../../utils/offlineReplayStatuses'
import { getDeviceSubtype } from '../../selectors/config'
import DeviceSubtypes from '../../utils/deviceSubtypes'
import { customHistory } from '../../stores'

const getOrder = makeGetOrder()

const directTo = (method = 'push', path, state) => {
  if (path === undefined) return

  customHistory[method](path, state)
}

// We just sent a request to FreedomPay to replay all offline approvals fro mthe card reader.
// Here we check the status for particular orders after that is initiated.
export function* getReplayAuthStatus(params = {}) {
  // wait 10 seconds before starting to give replayRequests some time to run first
  // we should re-do this logic when we can get a response from the replayRequests call
  yield delay(10000)

  const id = params.orderId
  const order = yield select(getOrder, id)

  const offlineRequestId = get(order, 'cardReaderData.requestId')
  if (!offlineRequestId) {
    yield put(replayAuthRequestFailed({ order, replayStatus: { statusDisplay: 'No request ID for card reader' } }))
    return
  }

  const bridgeInstance = PeripheralBridge.getBridge()
  if (!bridgeInstance) {
    yield put(replayAuthRequestFailed({ order, replayStatus: { statusDisplay: 'Could not reach the card reader' } }))
    return
  }

  const checkStatusPromise = new Promise((resolve, reject) => {
    bridgeInstance.callHandler( 'checkStatus', { 'requestId': offlineRequestId }, (data) => resolve(data) )
  })

  const statusData = yield checkStatusPromise

  const replayStatus = {
    ...statusData,
    statusDisplay: responseStatusString(statusData.responseStatus),
  }

  if (statusCheckComplete(statusData.responseStatus)) {
    let createOrderParams = order.createOrderParams

    // update order state for stadium based on freedompay status
    const isDeclined = approvedOfflineThenDeclined(statusData.responseStatus)
    createOrderParams.state = isDeclined ? 'authorization_failed' : 'completed'

    // need to update the freedom pay request id for refunds to work
    createOrderParams.userAttributes.freedompayRequestId = statusData.realRequestId
    const cardReaderData = { ...order.cardReaderData, requestId: statusData.realRequestId }

    yield put(saveOrder({ ...order, createOrderParams, cardReaderData, isDeclined }))
    yield put(replayAuthRequestSucceeded({ replayStatus, order }))
    yield put(syncOfflineOrderRequested(id))
  } else {
    yield put(replayAuthRequestFailed({ replayStatus, order }))
  }
}

export function* watchReplayAuthStatus() {
  yield takeEvery(REPLAY_AUTH_REQUESTED, getReplayAuthStatus)
}

export function* sendOrderEmail(params = {}) {
  const payload = params.payload

  try {
    yield call(Remote.sendEmailJobToStadium, payload)
    toast("Email Sent!")
  } catch (err) {
    toast("Email failed to send: " + err)
  }
  return
}

export function* watchSendOrderEmail() {
  yield takeEvery(SEND_ORDER_EMAIL, sendOrderEmail)
}

// Create orders in stadium. Payment is completed (online approval or paid with cash).
export function* syncOrder(params = {}) {
  const order = yield select(state => getOrder(state, params.id))

  if (order.isPreOrder) return

  const syncedOrder = {
    ...order
  }

  const payload = {
    ...order.createOrderParams,
    standUuid: null,
  }

  try {
    const result = yield call(Stadium.createPosOrder, payload)
    const { state, signatureRequired, isNoTipMenu } = order
    yield put(syncOrderSucceeded(result, syncedOrder))
    const currentOrderId = yield select(getCurrentOrderId)
    if (syncedOrder?.uuid === currentOrderId) {
      const applied = result?.data?.applied
      const friendlyMessage = result?.data?.friendlyMessage
      const applicationMessage = result?.data?.applicationMessage
      const errorMessage = friendlyMessage ?? applicationMessage
      if (applied || result?.status === 201) {
        if (errorMessage) {
          toast.error(errorMessage, { autoClose: false })
        } else {
          toast.success("Order creation was Successful", { autoClose: 2500 })
          if(!signatureRequired && isNoTipMenu && state !== 'tabbed') {
            yield call(directTo, 'push', `/receipt/${currentOrderId}`)
          }
        }
      } else {
        toast.error(errorMessage ?? 'Order Creation Failed', { autoClose: false })
        yield call(directTo, 'push', `/tabs/${currentOrderId}`)
      }
    }
  } catch (err) {
    yield put(syncOrderFailed(err, syncedOrder))
    const currentOrderId = yield select(getCurrentOrderId)
    if (syncedOrder?.uuid === currentOrderId) {
      const errorMessage = err?.response?.data?.errorMessage ?? "An error occured with order sync"
      toast.error(`${errorMessage}`, { autoClose: false })
    }
  }
}

export function* watchSyncOrder() {
  const queue = yield actionChannel(SYNC_ORDER_REQUESTED)

  while (true) {
    const payload = yield take(queue)

    yield call(syncOrder, payload)
  }
}

// Create order in stadium if payment is approved offline, or if the initial create request failed.
// Update order in stadium if payment was approved offline and is now finished (either failed or approved from card reader)
// For updates, we need to make sure the order is first created in Stadium so we have an offline_approval state transition
export function* syncOfflineOrder(params = {}) {
  const order = yield select(state => getOrder(state, params.id))

  // Order was created in stadium with offline_approval state; need to update it
  if (order.wasCreatedInStadium) {
    try {
      const result = yield call(Stadium.updatePosOrder, order.uuid, order.createOrderParams)
      yield put(updateOrderSucceeded(result, order.uuid))
    } catch (err) {
      yield put(updateOrderFailed(err, order.uuid))
    }

    return
  }

  try {
    const result = yield call(Stadium.createPosOrder, { ...order.createOrderParams, state: 'approved_offline' })
    yield put(createOfflineOrderSucceeded(result, order.uuid))

    const finished = ['authorization_failed', 'completed'].includes(order.createOrderParams.state)
    if (finished) {
      yield put(syncOrderInitiated(order.uuid))
      yield put(syncOfflineOrderRequested(order.uuid))
    }
  } catch (err) {
    yield put(createOfflineOrderFailed(err, order.uuid))
  }
}

export function* watchSyncOfflineOrder() {
  const queue = yield actionChannel(SYNC_OFFLINE_ORDER_REQUESTED)

  while (true) {
    const payload = yield take(queue)

    yield call(syncOfflineOrder, payload)
  }
}

export function* getPreOrders(params = {}) {
  const venueUuid = yield select((state) => get(state, 'config.venueUuid', ''))
  const standMenuUuids = yield select((state) => get(state, 'config.configuration.menuUuids', []))
  const salesEventId = get(params, 'payload.salesEventId', '')
  const standUuid = get(params, 'payload.standUuids', [])
  const deviceSubtype = yield select(getDeviceSubtype)

  let requestPayload = { venueUuid, standMenuUuids }

  if (DeviceSubtypes.isSuites(deviceSubtype)) {
    requestPayload = { venueUuid, salesEventId, standUuid }
  }

  try {
    const result = yield call(Remote.getPreOrders, requestPayload)
    const mappedResult = DataMapper.preOrderMapper(get(result, 'data', []))

    yield put(loadPreOrdersSucceeded(mappedResult))
  } catch (err) {
    yield put(loadPreOrdersFailed(err))
    toast.error("Orders failed to load")
  }
}

export function* watchGetPreOrders() {
  yield takeLeading(PRE_ORDERS_REQUESTED, getPreOrders)
}

export function* getOrderDetails(params = {}) {
  const unsyncedOrdersList = yield select(getUnsyncedLocalIds) ?? []

  try {
    const result = yield call(Remote.getOrderDetails, params.payload.orderUuid)
    const mappedResult = DataMapper.orderMapper(get(result, 'data', {}))
    if (unsyncedOrdersList.includes(params.payload.orderUuid)) {
      yield put(syncOrderInitiated(params.payload.orderUuid))
      yield put(syncOrderRequested(params.payload.orderUuid))
    }
    yield put(loadOrderDetailsSucceeded(mappedResult))
  } catch (err) {
    const order = yield select(getOrder, params.payload.orderUuid)
    if (order?.errorMessage) {
      toast.error(`Order Number ${order?.orderNumber}: ${order.errorMessage}`)
    }
    yield put(loadOrderDetailsFailed({ orderUuid: params.payload.orderUuid }))
  }
}

export function* watchGetOrderDetails() {
  const queue = yield actionChannel(ORDER_DETAILS_REQUESTED)

  while (true) {
    const payload = yield take(queue)

    yield call(getOrderDetails, payload)
  }
}

// params.since : int : time in milliseconds of when you want to backup and get the deltas from
// if none is provided, then it'll take the current timestamp
export function* getOrdersDeltas(params = {}) {
  const venueUuid = yield select((state) => get(state, 'config.venueUuid', ''))
  const orderDeltasSyncSuccessTimestamp = yield select((state) => get(state, 'order.orderDeltasSyncSuccessAt', null))

  try {
    const result = yield call(Remote.getOrderDeltas, venueUuid, orderDeltasSyncSuccessTimestamp)

    const deltas = get(result, 'data', {})
    yield put(loadOrdersDeltasSucceeded(deltas))

    const getAttendantStandUuids = makeGetAttendantStandUuids()
    const attendantStandUuids = yield select(getAttendantStandUuids)

    // looping through all the deltas to retrieve their order details from the API
    // this is a regular loop since it needs to yield and this generator function is being called
    // by an interval.
    for (let i = 0; i <= deltas.length - 1; i++ ) {
      const standUuid = get(deltas, [i, 'standUuid'])

      if (attendantStandUuids.includes(standUuid)) {
        yield call(getOrderDetails, {'payload': { 'orderUuid': deltas[i].uuid }})
      }
    }

  } catch (err) {
    yield put(loadOrdersDeltasFailed({ error: err, venueUuid: venueUuid }))
  }
}

export function* watchGetOrdersDeltas() {
  const queue = yield actionChannel(ORDERS_DELTAS_REQUESTED)

  while (true) {
    const payload = yield take(queue)

    yield call(getOrdersDeltas, payload)
  }
}
